<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import ScheduledMessagesAPI from 'custom/api/scheduledMessages';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import ScheduleMessageDialog from './ScheduleMessageDialog.vue';
import { SCHEDULE_EVENTS, formatWhen } from './helpers';

// Above the reply box: the conversation's upcoming scheduled messages, with their actions
const { t, locale } = useLocoI18n();
const currentChat = useMapGetter('getSelectedChat');

const REFRESH_MS = 60 * 1000;

const items = ref([]);
const expanded = ref(false);
const busyId = ref(null);
const dialogRef = ref(null);
let timer = null;

const conversationId = computed(() => currentChat.value?.id);
const next = computed(() =>
  items.value.find(item => item.status === 'scheduled')
);
const browserLocale = computed(() => locale.value.replace('_', '-'));

const load = async () => {
  if (!conversationId.value) return;
  const requested = conversationId.value;
  try {
    const { data } = await ScheduledMessagesAPI.forConversation(requested);
    if (requested === conversationId.value) items.value = data.payload;
  } catch {
    // The bar is informational; a failed refresh keeps the previous list
  }
};

const run = async (item, request) => {
  busyId.value = item.id;
  try {
    await request();
    await load();
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    busyId.value = null;
  }
};

const sendNow = item => run(item, () => ScheduledMessagesAPI.sendNow(item.id));
const togglePause = item =>
  run(item, () =>
    ScheduledMessagesAPI.update(item.id, {
      status: item.status === 'paused' ? 'scheduled' : 'paused',
    })
  );
const remove = item => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('LOCO.SCHEDULED.BAR.DELETE_CONFIRM'))) return;
  run(item, () => ScheduledMessagesAPI.delete(item.id));
};
const edit = item => dialogRef.value.open({ scheduledMessage: item });

const onChanged = id => {
  if (!id || id === conversationId.value) load();
};

watch(conversationId, () => {
  items.value = [];
  expanded.value = false;
  load();
});

onMounted(() => {
  load();
  timer = setInterval(load, REFRESH_MS);
  emitter.on(SCHEDULE_EVENTS.CHANGED, onChanged);
});

onBeforeUnmount(() => {
  clearInterval(timer);
  emitter.off(SCHEDULE_EVENTS.CHANGED, onChanged);
});
</script>

<template>
  <div v-if="items.length" class="mx-2 mb-1">
    <button
      type="button"
      class="flex items-center w-full gap-2 px-3 py-1.5 text-xs rounded-lg text-n-slate-11 hover:bg-n-alpha-1"
      @click="expanded = !expanded"
    >
      <Icon icon="i-lucide-clock" class="size-3.5 flex-shrink-0" />
      <span class="font-medium text-n-slate-12">
        {{
          t('LOCO.SCHEDULED.BAR.TITLE', { count: items.length }, items.length)
        }}
      </span>
      <span v-if="next">
        ·
        {{
          t('LOCO.SCHEDULED.BAR.NEXT', {
            when: formatWhen(next.next_run_at, browserLocale),
          })
        }}
      </span>
      <Icon
        :icon="expanded ? 'i-lucide-chevron-down' : 'i-lucide-chevron-up'"
        class="size-3.5 ms-auto"
      />
    </button>

    <ul
      v-if="expanded"
      class="flex flex-col gap-1 mt-1 max-h-64 overflow-y-auto rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 p-1"
    >
      <li
        v-for="item in items"
        :key="item.id"
        class="flex items-start gap-3 p-2 rounded-md hover:bg-n-alpha-1"
      >
        <div class="flex flex-col flex-1 min-w-0 gap-0.5">
          <div
            class="flex flex-wrap items-center gap-2 text-xs text-n-slate-11"
          >
            <span class="font-medium text-n-slate-12">
              {{ formatWhen(item.next_run_at, browserLocale) }}
            </span>
            <span v-if="item.recurrence">
              {{ t(`LOCO.SCHEDULED.BAR.REPEATS.${item.recurrence.frequency}`) }}
            </span>
            <span v-if="item.sent_count">
              {{
                t('LOCO.SCHEDULED.BAR.SENT_COUNT', { count: item.sent_count })
              }}
            </span>
            <span v-if="item.private" class="text-n-amber-11">
              {{ t('LOCO.SCHEDULED.BAR.PRIVATE') }}
            </span>
            <span v-if="item.status === 'paused'" class="text-n-amber-11">
              {{ t('LOCO.SCHEDULED.BAR.PAUSED') }}
            </span>
            <span v-if="item.last_error" class="text-n-ruby-11 truncate">
              {{ t('LOCO.SCHEDULED.BAR.FAILED', { error: item.last_error }) }}
            </span>
          </div>
          <p class="text-sm text-n-slate-12 truncate">
            {{
              item.content || item.files.map(file => file.filename).join(', ')
            }}
          </p>
        </div>
        <div class="flex gap-1 flex-shrink-0">
          <Button
            v-tooltip.top="t('LOCO.SCHEDULED.BAR.SEND_NOW')"
            xs
            ghost
            slate
            icon="i-lucide-send"
            :is-loading="busyId === item.id"
            @click="sendNow(item)"
          />
          <Button
            v-tooltip.top="
              item.status === 'paused'
                ? t('LOCO.SCHEDULED.BAR.RESUME')
                : t('LOCO.SCHEDULED.BAR.PAUSE')
            "
            xs
            ghost
            slate
            :icon="
              item.status === 'paused' ? 'i-lucide-play' : 'i-lucide-pause'
            "
            :disabled="busyId === item.id"
            @click="togglePause(item)"
          />
          <Button
            v-tooltip.top="t('LOCO.SCHEDULED.BAR.EDIT')"
            xs
            ghost
            slate
            icon="i-lucide-pencil"
            :disabled="busyId === item.id"
            @click="edit(item)"
          />
          <Button
            v-tooltip.top="t('LOCO.SCHEDULED.BAR.DELETE')"
            xs
            ghost
            ruby
            icon="i-lucide-trash-2"
            :disabled="busyId === item.id"
            @click="remove(item)"
          />
        </div>
      </li>
    </ul>

    <ScheduleMessageDialog
      v-if="conversationId"
      ref="dialogRef"
      :conversation-id="conversationId"
    />
  </div>
</template>
